import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Sparkles, RefreshCw, PlayCircle, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateTime } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/maintenance/article-sanitation`;

const Stat = ({ label, value, ok, testId }) => (
  <div className={`border rounded p-2 ${ok ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`}>
    <div className="text-[11px] text-muted-foreground">{label}</div>
    <div className={`text-2xl font-bold ${ok ? 'text-emerald-800' : 'text-amber-800'}`} data-testid={testId}>{value}</div>
  </div>
);

const DuplicatesTable = ({ groups }) => (
  <Table data-testid="sanitation-duplicates-table">
    <TableHeader>
      <TableRow>
        <TableHead>Ficha a absorber</TableHead>
        <TableHead>Origen</TableHead>
        <TableHead className="text-right">Lotes</TableHead>
        <TableHead>Se une a (ficha buena)</TableHead>
        <TableHead>Motivo</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {groups.flatMap(g => g.absorbed.map(a => (
        <TableRow key={a.article_id} data-testid={`sanitation-dup-row-${a.article_id}`}>
          <TableCell className="font-medium">{a.name}<div className="text-[10px] text-muted-foreground">{a.warehouse}</div></TableCell>
          <TableCell className="text-xs">{a.source_rem || '—'}{a.pending_review && <Badge className="ml-1 bg-amber-500 text-white">sin validar</Badge>}</TableCell>
          <TableCell className="text-right">{a.lots}</TableCell>
          <TableCell className="font-medium">{g.primary.name}<div className="text-[10px] text-muted-foreground">{g.primary.warehouse} · {g.primary.lots} lotes</div></TableCell>
          <TableCell className="text-xs">{g.reason === 'alias' ? 'Nombre ya fusionado antes' : 'Mismo nombre, mismo depósito'}</TableCell>
        </TableRow>
      )))}
    </TableBody>
  </Table>
);

const RunStatus = ({ run }) => {
  if (!run) return null;
  const r = run.result;
  return (
    <div className={`rounded border p-3 text-xs space-y-1 ${run.status === 'error' ? 'border-rose-300 bg-rose-50' : run.status === 'done' ? 'border-emerald-300 bg-emerald-50' : 'border-blue-300 bg-blue-50'}`} data-testid="sanitation-run-status">
      <div className="font-semibold flex items-center gap-2">
        {run.status === 'running' && <Loader2 className="w-3 h-3 animate-spin" />}
        {run.status === 'done' && <CheckCircle2 className="w-3 h-3 text-emerald-700" />}
        {run.status === 'error' && <AlertTriangle className="w-3 h-3 text-rose-700" />}
        Última ejecución: {run.step}{run.status === 'running' && run.total > 0 && ` (${run.done}/${run.total})`}
      </div>
      <div className="text-muted-foreground">Por {run.started_by} · {fmtDateTime(run.started_at)}</div>
      {run.error && <div className="text-rose-700" data-testid="sanitation-run-error">{run.error}</div>}
      {r && (
        <ul className="list-disc list-inside" data-testid="sanitation-run-result">
          <li>Espejos consolidados: <b>{r.mirrors_consolidated}</b> (archivados: {r.mirrors_archived ?? 0} · sin ficha original: {r.mirror_orphans})</li>
          <li>Duplicados fusionados: <b>{r.duplicates_merged.reduce((s, m) => s + m.absorbed.length, 0)}</b></li>
          <li>Stock por depósito recalculado: {r.projection?.pairs_recomputed} pares</li>
          <li>Verificación: lotes activos con espejo {r.verify.orphan_mirror_lots_active} · duplicados pendientes {r.verify.duplicate_groups_remaining}</li>
          <li>Copia de seguridad: {r.snapshot_docs} registros (código {run.run_id})</li>
        </ul>
      )}
    </div>
  );
};

export const ArticleSanitationPanel = () => {
  const [preview, setPreview] = useState(null);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const poll = useRef(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/preview`, { withCredentials: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo analizar el catálogo');
    } finally { setLoading(false); }
  }, []);

  const loadRun = useCallback(async () => {
    const r = await axios.get(`${API}/runs/latest`, { withCredentials: true });
    setRun(r.data.run);
    return r.data.run;
  }, []);

  const startPolling = useCallback(() => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const cur = await loadRun().catch(() => null);
      if (cur && cur.status !== 'running') {
        clearInterval(poll.current);
        if (cur.status === 'done') toast.success('Saneamiento terminado');
        else toast.error('El saneamiento terminó con error');
        loadPreview();
      }
    }, 3000);
  }, [loadRun, loadPreview]);

  useEffect(() => {
    loadPreview();
    loadRun().then(r => { if (r?.status === 'running') startPolling(); }).catch(() => {});
    return () => clearInterval(poll.current);
  }, [loadPreview, loadRun, startPolling]);

  const apply = async () => {
    try {
      await axios.post(`${API}/apply`, { confirm: confirmText }, { withCredentials: true });
      setConfirmOpen(false);
      setConfirmText('');
      toast.info('Saneamiento iniciado');
      await loadRun();
      startPolling();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar');
    }
  };

  const m = preview?.mirrors;
  const d = preview?.duplicates;
  const pendingWork = preview && (m.pending > 0 || d.absorbed_count > 0 || preview.projection_drift > 0);
  const running = run?.status === 'running';

  return (
    <Card className={pendingWork ? 'border-amber-300' : 'border-emerald-300'} data-testid="article-sanitation-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" /> Saneamiento integral de artículos
            {preview && !pendingWork && <Badge className="bg-emerald-500 text-white" data-testid="sanitation-clean-badge">Todo en orden</Badge>}
          </span>
          <Button size="sm" variant="outline" onClick={loadPreview} disabled={loading || running} data-testid="sanitation-refresh-btn">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Analizar
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground leading-relaxed">
          En un solo paso y en este orden: 1) une los artículos espejo (<code>__d_</code>) a su ficha original, 2) fusiona las fichas duplicadas
          con su ficha buena (incluye las creadas por REM con un nombre que ya se había fusionado) y 3) recalcula el stock por depósito.
          Conserva lotes, vencimientos y movimientos, y guarda una copia de seguridad antes de cambiar nada.
        </p>
        {preview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="Espejos por consolidar" value={m.pending} ok={m.pending === 0} testId="sanitation-mirrors-pending" />
            <Stat label="Espejos ya consolidados" value={m.already_consolidated} ok testId="sanitation-mirrors-done" />
            <Stat label="Duplicados a fusionar" value={d.absorbed_count} ok={d.absorbed_count === 0} testId="sanitation-dups-count" />
            <Stat label="Stock por depósito a recalcular" value={preview.projection_drift} ok={preview.projection_drift === 0} testId="sanitation-drift-count" />
          </div>
        )}
        {m?.orphans_count > 0 && (
          <div className="text-xs text-rose-700" data-testid="sanitation-orphans">{m.orphans_count} espejo(s) sin ficha original: no se tocan, requieren revisión manual.</div>
        )}
        {d?.groups?.length > 0 && <DuplicatesTable groups={d.groups} />}
        <RunStatus run={run} />
        <div className="flex justify-end">
          <Button onClick={() => setConfirmOpen(true)} disabled={!pendingWork || running || loading} data-testid="sanitation-apply-btn">
            <PlayCircle className="w-4 h-4 mr-1" /> Aplicar saneamiento
          </Button>
        </div>
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="sanitation-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar saneamiento</DialogTitle>
            <DialogDescription>
              Se van a consolidar {m?.pending} espejo(s), fusionar {d?.absorbed_count} ficha(s) duplicada(s) y recalcular {preview?.projection_drift} par(es) de stock.
              Antes se guarda una copia de seguridad. Escribí <b>SANEAR</b> para continuar.
            </DialogDescription>
          </DialogHeader>
          <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="SANEAR" data-testid="sanitation-confirm-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="sanitation-confirm-cancel">Cancelar</Button>
            <Button onClick={apply} disabled={confirmText.trim().toUpperCase() !== 'SANEAR'} data-testid="sanitation-confirm-submit">Aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
